import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole, logError } from "../../utils/customLogger";
import { getAuthHeaders, handleApiResponse } from "../utils/apiHelpers";

export interface MedicationOption {
  label: string;
  value: string;
}

/**
 * Searches for medications via the internal drug-search API.
 *
 * @param query - Partial drug name to search for
 * @param signal - Optional AbortSignal for request cancellation
 * @returns List of matching medication options
 */
export const searchMedications = async (
  query: string,
  signal?: AbortSignal,
): Promise<MedicationOption[]> => {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const encodedQuery = encodeURIComponent(query.trim());
  const url = `${API_ENDPOINTS.DRUG_SEARCH}?query=${encodedQuery}`;
  const authHeaders = await getAuthHeaders();

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { ...authHeaders },
      signal,
    });
    const data = await handleApiResponse(response);
    logDataToConsole("Drug search", data);
    return normalizeDrugSearchResponse(data);
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      throw error;
    }
    logError(error as Error, "Error in searchMedications");
    return [];
  }
};

interface DrugSearchResult {
  id: string;
  name: string;
  full_name: string;
  ndc: string;
  strength: string;
  strength_unit: string;
  dosage_form: string;
  [key: string]: unknown;
}

/**
 * Normalizes the drug-search API response into a deduplicated list of MedicationOption.
 * Uses `full_name` (e.g. "ALPRAZolam 0.25MG tablets") so each strength/form is a distinct option.
 *
 * @param response - Raw API response shaped as { results: DrugSearchResult[] }
 * @returns Deduplicated medication options keyed by full_name
 */
function normalizeDrugSearchResponse(response: unknown): MedicationOption[] {
  if (response == null) return [];

  const rows = extractResultsArray(response);
  const seen = new Set<string>();
  const options: MedicationOption[] = [];

  for (const row of rows) {
    const label = row.full_name || row.name || "";
    if (!label) continue;

    const key = label.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);

    options.push({ label, value: label });
  }

  return options;
}

/**
 * Extracts the results array from the API envelope.
 * Supports `{ results: [...] }`, `{ data: [...] }`, `{ rows: [...] }`, or a bare array.
 *
 * @param response - Raw parsed JSON from the drug-search endpoint
 * @returns Array of drug search result objects
 */
function extractResultsArray(response: unknown): DrugSearchResult[] {
  if (Array.isArray(response)) return response as DrugSearchResult[];

  if (typeof response === "object" && response !== null) {
    const obj = response as Record<string, unknown>;
    const candidates = [obj.results, obj.data, obj.rows, obj.result];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate as DrugSearchResult[];
    }
  }

  return [];
}
